import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const PageContainer = styled.div`
  padding: 120px 0 60px;
  background-color: var(--bg-light);
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-dark);
  margin-bottom: 2rem;
  text-align: center;
`;

const ContentSection = styled.section`
  background: var(--white);
  padding: 2.5rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  margin-top: 2.5rem;
  
  &:first-child {
    margin-top: 0;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

const List = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
`;

const ListItem = styled.li`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.8;
  margin-bottom: 0.75rem;
`;

const BackButton = styled(Link)`
  display: inline-block;
  background: linear-gradient(to right, var(--primary-color), var(--secondary-color));
  color: var(--white);
  font-weight: 600;
  padding: 0.875rem 1.75rem;
  border-radius: var(--border-radius);
  text-decoration: none;
  margin-bottom: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(var(--primary-rgb), 0.3);
  }
`;

const LastUpdated = styled.p`
  font-size: 0.9rem;
  color: var(--text-light);
  text-align: center;
  margin-top: 3rem;
  font-style: italic;
`;

const TermsOfService = () => {
  return (
    <PageContainer>
      <Container>
        <BackButton to="/">← Back to Home</BackButton>
        <PageTitle>Terms of Service</PageTitle>
        
        <ContentSection>
          <Paragraph>
            Welcome to tinysmart.AI. These Terms of Service ("Terms") govern your access to and use of the tinysmart.AI platform, 
            applications, and services (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms.
          </Paragraph>
          
          <SectionTitle>1. Acceptance of Terms</SectionTitle>
          <Paragraph>
            By accessing or using tinysmart.AI, you acknowledge that you have read, understood, and agree to be bound by these Terms. 
            If you do not agree to these Terms, you may not access or use the Service.
          </Paragraph>
          
          <SectionTitle>2. Changes to Terms</SectionTitle>
          <Paragraph>
            We reserve the right to modify these Terms at any time. We will provide notice of any material changes by posting the updated 
            Terms on this page and updating the "Last Updated" date. Your continued use of the Service after such changes constitutes your 
            acceptance of the new Terms.
          </Paragraph>
          
          <SectionTitle>3. Account Registration</SectionTitle>
          <Paragraph>
            To access certain features of the Service, you may need to register for an account. You agree to provide accurate, current, 
            and complete information during the registration process and to update such information to keep it accurate, current, and complete.
          </Paragraph>
          <Paragraph>
            You are responsible for safeguarding your account credentials and for all activities that occur under your account. 
            You agree to notify us immediately of any unauthorized use of your account.
          </Paragraph>
          
          <SectionTitle>4. Use of the Service</SectionTitle>
          <Paragraph>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:</Paragraph>
          <List>
            <ListItem>Use the Service in any way that violates any applicable law or regulation</ListItem>
            <ListItem>Use the Service to send, upload, or distribute any viruses, malware, or other harmful computer code</ListItem>
            <ListItem>Attempt to gain unauthorized access to any portion of the Service or any other systems or networks connected to the Service</ListItem>
            <ListItem>Interfere with or disrupt the Service or servers or networks connected to the Service</ListItem>
            <ListItem>Use the Service to harass, abuse, or harm another person</ListItem>
            <ListItem>Use the Service for any purpose that is harmful, fraudulent, deceptive, or otherwise objectionable</ListItem>
          </List>
          
          <SectionTitle>5. Intellectual Property Rights</SectionTitle>
          <Paragraph>
            The Service and its content, features, and functionality are owned by tinysmart.AI and are protected by copyright, trademark, 
            and other intellectual property rights. You may not copy, modify, distribute, sell, or lease any part of the Service without our 
            prior written consent.
          </Paragraph>
          
          <SectionTitle>6. User Content</SectionTitle>
          <Paragraph>
            You retain all rights in, and are solely responsible for, the content you create, post, upload, or otherwise make available through 
            the Service ("User Content"). By posting User Content, you grant us a non-exclusive, worldwide, royalty-free license to use, 
            reproduce, modify, adapt, publish, translate, and distribute your User Content in connection with providing and improving the Service.
          </Paragraph>
          
          <SectionTitle>7. AI-Generated Content</SectionTitle>
          <Paragraph>
            The Service includes AI agents that generate content ("AI-Generated Content") in response to your inputs. You acknowledge that 
            AI-Generated Content:
          </Paragraph>
          <List>
            <ListItem>May not always be accurate or appropriate</ListItem>
            <ListItem>Should not be relied upon for critical decisions without verification</ListItem>
            <ListItem>Is provided "as is" without warranties of any kind</ListItem>
          </List>
          <Paragraph>
            You are responsible for reviewing and evaluating all AI-Generated Content before use.
          </Paragraph>
          
          <SectionTitle>8. Privacy</SectionTitle>
          <Paragraph>
            Your use of the Service is also governed by our Privacy Policy, which can be found at <Link to="/privacy-policy" style={{ color: 'var(--primary-color)' }}>Privacy Policy</Link>. 
            By using the Service, you consent to the collection and use of information as detailed in our Privacy Policy.
          </Paragraph>
          
          {/* <SectionTitle>9. Disclaimer of Warranties</SectionTitle> */}
          <Paragraph>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, 
            BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
          </Paragraph>
          <Paragraph>
            tinysmart.AI DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT ANY DEFECTS WILL BE CORRECTED.
          </Paragraph>
          
          <SectionTitle>10. Limitation of Liability</SectionTitle>
          <Paragraph>
            IN NO EVENT SHALL tinysmart.AI BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, 
            INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF OR IN CONNECTION WITH THE SERVICE, WHETHER 
            BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY, EVEN IF tinysmart.AI HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </Paragraph>
          
          <SectionTitle>11. Indemnification</SectionTitle>
          <Paragraph>
            You agree to indemnify, defend, and hold harmless tinysmart.AI and its officers, directors, employees, agents, and affiliates 
            from and against any and all claims, damages, obligations, losses, liabilities, costs, or debt, and expenses (including but not 
            limited to attorney's fees) arising from: (i) your use of and access to the Service; (ii) your violation of any term of these Terms; 
            (iii) your violation of any third-party right, including without limitation any copyright, property, or privacy right; or (iv) any 
            claim that your User Content caused damage to a third party.
          </Paragraph>
          
          <SectionTitle>12. Termination</SectionTitle>
          <Paragraph>
            We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, 
            including without limitation if you breach these Terms. Upon termination, your right to use the Service will immediately cease.
          </Paragraph>
          
          <SectionTitle>13. Governing Law</SectionTitle>
          <Paragraph>
            These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions.
          </Paragraph>
          
          <SectionTitle>14. Contact Us</SectionTitle>
          <Paragraph>
            If you have any questions about these Terms, please contact us at contact@tinysmart.ai.
          </Paragraph>
          
          <LastUpdated>Last Updated: April 21, 2025</LastUpdated>
        </ContentSection>
      </Container>
    </PageContainer>
  );
};

export default TermsOfService; 